import React, { useState, useMemo } from 'react';
import { Table, Card, Statistic, Button, Input, Space, Spin, Row, Col, Modal, Descriptions, Tag, Divider } from 'antd';
import { SearchOutlined, DownloadOutlined, ShoppingOutlined, UserOutlined, ShopOutlined, CalendarOutlined, DollarOutlined, TagOutlined, FileTextOutlined } from '@ant-design/icons';
import ChartWrapper from './ChartWrapper';

const OrdersDashboard = ({
    data = [],
    title = "Recent Orders",
    loading = false,
    link,
    showTopCount = 5,
    showFullTable = false
}) => {
    const [expanded, setExpanded] = useState(showFullTable);
    const [searchText, setSearchText] = useState('');
    const [selectedStatus, setSelectedStatus] = useState('all');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);

    // Calculate summary statistics
    const summaryStats = useMemo(() => {
        if (!data || data.length === 0) {
            return {
                totalOrders: 0,
                totalSales: 0,
                avgOrderValue: 0,
                completedOrders: 0
            };
        }

        const totalOrders = data.length;
        const totalSales = data.reduce((sum, order) => sum + order.NetAmount, 0);
        const avgOrderValue = totalSales / totalOrders;
        const completedOrders = data.filter(order =>
            order.OrderStatus === 'Confirmed' || order.OrderStatus === 'Completed'
        ).length;

        return {
            totalOrders,
            totalSales: parseFloat(totalSales.toFixed(2)),
            avgOrderValue: parseFloat(avgOrderValue.toFixed(2)),
            completedOrders,
            pendingOrders: totalOrders - completedOrders
        };
    }, [data]);

    // Filter and search data
    const filteredData = useMemo(() => {
        let filtered = data;

        // Filter by search text
        if (searchText) {
            const searchLower = searchText.toLowerCase();
            filtered = filtered.filter(order =>
                order.OrderNumber.toLowerCase().includes(searchLower) ||
                order.BranchName.toLowerCase().includes(searchLower) ||
                order.OrderMode.toLowerCase().includes(searchLower) ||
                order.PaymentMode?.toLowerCase().includes(searchLower) ||
                order.OrderDetails?.some(detail =>
                    detail.ProductName.toLowerCase().includes(searchLower)
                )
            );
        }

        // Filter by status
        if (selectedStatus !== 'all') {
            filtered = filtered.filter(order => order.OrderStatus === selectedStatus);
        }

        return filtered;
    }, [data, searchText, selectedStatus]);

    // Get unique statuses for filter
    const statusOptions = useMemo(() => {
        const statuses = [...new Set(data.map(order => order.OrderStatus).filter(Boolean))];
        return ['all', ...statuses];
    }, [data]);

    // Display data based on expanded state
    const displayData = expanded ? filteredData : filteredData.slice(0, showTopCount);

    // Handle view order details
    const handleViewOrder = (order) => {
        setSelectedOrder(order);
        setIsModalVisible(true);
    };

    // Format date
    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Table columns
    const columns = [
        {
            title: 'Order #',
            dataIndex: 'OrderNumber',
            key: 'OrderNumber',
            sorter: (a, b) => a.OrderNumber.localeCompare(b.OrderNumber),
            width: 150,
        },
        {
            title: 'Date & Time',
            dataIndex: 'OrderDateTime',
            key: 'OrderDateTime',
            render: (text) => formatDate(text),
            width: 140,
        },
        {
            title: 'Branch',
            dataIndex: 'BranchName',
            key: 'BranchName',
            width: 100,
        },
        {
            title: 'Status',
            dataIndex: 'OrderStatus',
            key: 'OrderStatus',
            render: (status) => (
                <Tag color={
                    status === 'Confirmed' ? 'success' :
                        status === 'Completed' ? 'green' :
                            status === 'Pending' ? 'orange' :
                                status === 'Cancelled' ? 'red' : 'default'
                }>
                    {status}
                </Tag>
            ),
            width: 110,
        },
        {
            title: 'Mode',
            dataIndex: 'OrderMode',
            key: 'OrderMode',
            width: 90,
        },
        {
            title: 'Items',
            key: 'itemsCount',
            render: (_, record) => record.OrderDetails?.length || 0,
            width: 80,
        },
        {
            title: 'Amount',
            dataIndex: 'NetAmount',
            key: 'NetAmount',
            render: (amount) => `PKR ${amount.toFixed(2)}`,
            sorter: (a, b) => a.NetAmount - b.NetAmount,
            width: 120,
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (_, record) => (
                <Button
                    type="link"
                    size="small"
                    onClick={() => handleViewOrder(record)}
                >
                    View Details
                </Button>
            ),
            width: 100,
        },
    ];

    // Order Details Modal
    const OrderDetailsModal = ({ order, visible, onClose }) => {
        if (!order) return null;

        const calculateItemTotal = (item) => {
            return (item.Quantity * item.PriceWithGST).toFixed(2);
        };

        const orderTotal = order.NetAmount;
        const itemsTotal = order.OrderDetails?.reduce((sum, item) =>
            sum + (item.Quantity * item.PriceWithGST), 0
        ) || 0;

        return (
            <Modal
                title={
                    <div>
                        <FileTextOutlined style={{ marginRight: 8 }} />
                        Order Details: {order.OrderNumber}
                    </div>
                }
                open={visible}
                onCancel={onClose}
                footer={[
                    <Button key="close" onClick={onClose}>
                        Close
                    </Button>,
                    <Button key="print" type="primary" onClick={() => window.print()}>
                        Print
                    </Button>
                ]}
                width={800}
                centered
            >
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {/* Order Summary */}
                    <Card size="small" style={{ marginBottom: 16 }}>
                        <Row gutter={[16, 16]}>
                            <Col span={12}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label={<><CalendarOutlined /> Order Date</>}>
                                        {formatDate(order.OrderDateTime)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<><ShopOutlined /> Branch</>}>
                                        {order.BranchName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<><TagOutlined /> Order Mode</>}>
                                        {order.OrderMode}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Order Source">
                                        {order.OrderSource}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Col>
                            <Col span={12}>
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Order Status">
                                        <Tag color={
                                            order.OrderStatus === 'Confirmed' ? 'success' :
                                                order.OrderStatus === 'Completed' ? 'green' :
                                                    order.OrderStatus === 'Pending' ? 'orange' :
                                                        order.OrderStatus === 'Cancelled' ? 'red' : 'default'
                                        }>
                                            {order.OrderStatus}
                                        </Tag>
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Shift">
                                        {order.ShiftName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Terminal">
                                        {order.TerminalName}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Payment Mode">
                                        {order.PaymentMode || 'N/A'}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Col>
                        </Row>
                    </Card>

                    {/* Order Items */}
                    <Divider orientation="left">Order Items</Divider>
                    <Table
                        dataSource={order.OrderDetails || []}
                        columns={[
                            {
                                title: 'Product',
                                dataIndex: 'ProductName',
                                key: 'ProductName',
                                render: (text, record) => (
                                    <div>
                                        <strong>{text}</strong>
                                        <div style={{ fontSize: '12px', color: '#666' }}>
                                            {record.SizeName && record.SizeName !== '-' ? `Size: ${record.SizeName}` : ''}
                                            {record.FlavourName && record.FlavourName !== '-' ? ` | Flavour: ${record.FlavourName}` : ''}
                                        </div>
                                    </div>
                                ),
                            },
                            {
                                title: 'Category',
                                dataIndex: 'CategoryName',
                                key: 'CategoryName',
                                width: 120,
                            },
                            {
                                title: 'Dept',
                                dataIndex: 'DepartmentName',
                                key: 'DepartmentName',
                                width: 100,
                            },
                            {
                                title: 'Qty',
                                dataIndex: 'Quantity',
                                key: 'Quantity',
                                width: 80,
                                render: (qty) => qty.toFixed(0),
                            },
                            {
                                title: 'Unit Price',
                                dataIndex: 'PriceWithGST',
                                key: 'PriceWithGST',
                                width: 100,
                                render: (price) => `PKR ${price.toFixed(2)}`,
                            },
                            {
                                title: 'Discount %',
                                dataIndex: 'DiscountPercent',
                                key: 'DiscountPercent',
                                width: 80,
                                render: (discount) => `${discount}%`,
                            },
                            {
                                title: 'Total',
                                key: 'total',
                                width: 100,
                                render: (_, item) => `PKR ${calculateItemTotal(item)}`,
                            },
                        ]}
                        pagination={false}
                        size="small"
                        rowKey="OrderDetailId"
                        summary={() => (
                            <Table.Summary fixed>
                                <Table.Summary.Row>
                                    <Table.Summary.Cell index={0} colSpan={4}>
                                        <strong>Items Total</strong>
                                    </Table.Summary.Cell>
                                    <Table.Summary.Cell index={4}>
                                        <strong>
                                            PKR {itemsTotal.toFixed(2)}
                                        </strong>
                                    </Table.Summary.Cell>
                                </Table.Summary.Row>
                            </Table.Summary>
                        )}
                    />

                    {/* Financial Summary */}
                    <Divider orientation="left">Financial Summary</Divider>
                    <Row gutter={[16, 8]} style={{ marginBottom: 16 }}>
                        <Col span={12}>
                            <Card size="small">
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Subtotal">
                                        PKR {order.SubTotal.toFixed(2)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="GST">
                                        PKR {order.Gst.toFixed(2)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Delivery Charges">
                                        PKR {order.DeliveryCharges.toFixed(2)}
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Col>
                        <Col span={12}>
                            <Card size="small">
                                <Descriptions column={1} size="small">
                                    <Descriptions.Item label="Discount">
                                        PKR {order.Discount.toFixed(2)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label="Tip">
                                        PKR {order.Tip.toFixed(2)}
                                    </Descriptions.Item>
                                    <Descriptions.Item label={<strong>Net Amount</strong>}>
                                        <strong style={{ color: '#1890ff', fontSize: '16px' }}>
                                            PKR {order.NetAmount.toFixed(2)}
                                        </strong>
                                    </Descriptions.Item>
                                </Descriptions>
                            </Card>
                        </Col>
                    </Row>

                    {/* Payment Information */}
                    <Card size="small" title="Payment Information">
                        <Descriptions column={2} size="small">
                            <Descriptions.Item label="Received Amount">
                                PKR {order.Received_Amount.toFixed(2)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Change Amount">
                                PKR {order.ChangeAmount.toFixed(2)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Business Date">
                                {order.BusinessDate ? formatDate(order.BusinessDate) : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Order Master ID">
                                {order.OrderMasterId}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                </div>
            </Modal>
        );
    };

    const tableContent = () => {
        if (loading) {
            return (
                <div className="flex justify-center items-center h-64">
                    <Spin size="large" />
                </div>
            );
        }

        if (!data || data.length === 0) {
            return (
                <div className="flex justify-center items-center h-64">
                    <div className="text-center">
                        <ShoppingOutlined style={{ fontSize: 48, color: '#ccc' }} />
                        <p className="mt-4 text-gray-500">No orders found</p>
                    </div>
                </div>
            );
        }

        return (
            <>
                <div className="mb-4">
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Orders"
                                    value={summaryStats.totalOrders}
                                    prefix={<ShoppingOutlined />}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Total Sales"
                                    value={summaryStats.totalSales}
                                    prefix="PKR"
                                    valueStyle={{ color: '#3f8600' }}
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Avg Order Value"
                                    value={summaryStats.avgOrderValue}
                                    prefix="PKR"
                                />
                            </Card>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Card size="small">
                                <Statistic
                                    title="Completed Orders"
                                    value={summaryStats.completedOrders}
                                    valueStyle={{ color: '#52c41a' }}
                                />
                            </Card>
                        </Col>
                    </Row>
                </div>

                <div className="mb-4">
                    <Space style={{ width: '100%', justifyContent: 'space-between', flexWrap: 'wrap' }}>
                        <Space>
                            <Input
                                placeholder="Search orders..."
                                prefix={<SearchOutlined />}
                                value={searchText}
                                onChange={(e) => setSearchText(e.target.value)}
                                style={{ width: 200 }}
                                allowClear
                            />
                            <select
                                value={selectedStatus}
                                onChange={(e) => setSelectedStatus(e.target.value)}
                                style={{
                                    padding: '6px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #d9d9d9',
                                    backgroundColor: 'white'
                                }}
                            >
                                {statusOptions.map(status => (
                                    <option key={status} value={status}>
                                        {status === 'all' ? 'All Status' : status}
                                    </option>
                                ))}
                            </select>
                        </Space>
                        <Space>
                            <Button
                                icon={<DownloadOutlined />}
                                size="small"
                                onClick={() => console.log('Export data')}
                            >
                                Export
                            </Button>
                            <Button
                                type="primary"
                                size="small"
                                onClick={() => setExpanded(!expanded)}
                            >
                                {expanded ? 'Show Less' : 'See More'}
                            </Button>
                        </Space>
                    </Space>
                </div>

                <Table
                    dataSource={displayData}
                    columns={columns}
                    pagination={expanded ? { pageSize: 10, showSizeChanger: true } : false}
                    size="small"
                    scroll={{ x: true }}
                    rowKey="OrderMasterId"
                    summary={() => (
                        <Table.Summary fixed>
                            <Table.Summary.Row>
                                <Table.Summary.Cell index={0} colSpan={5}>
                                    <strong>Showing {displayData.length} of {filteredData.length} orders</strong>
                                </Table.Summary.Cell>
                                <Table.Summary.Cell index={5}>
                                    <strong>
                                        Total: PKR {displayData.reduce((sum, order) => sum + order.NetAmount, 0).toFixed(2)}
                                    </strong>
                                </Table.Summary.Cell>
                            </Table.Summary.Row>
                        </Table.Summary>
                    )}
                />

                {/* Order Details Modal */}
                <OrderDetailsModal
                    order={selectedOrder}
                    visible={isModalVisible}
                    onClose={() => {
                        setIsModalVisible(false);
                        setSelectedOrder(null);
                    }}
                />
            </>
        );
    };

    return (
        <ChartWrapper
            title={title}
            link={link}
        >
            {tableContent()}
        </ChartWrapper>
    );
};

export default OrdersDashboard;